import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { partnerSession, requireDocflow, requirePartner } from "@/auth/middleware";
import { afterRequest, runAsServiceDetached, runInUserContext } from "@/db/client";
import {
  closingOpen,
  DEAL_STATUSES,
  PAPER_CARRIERS,
  PAYMENT_SCHEMES,
  type DealDraft,
  type DealStatus,
} from "./deals";
import {
  createKit,
  docflowPeople,
  listDeals,
  markArrived,
  moveDeal,
  myDeal,
  readDeal,
  freezeTexts,
  kitTexts,
  readChain,
  readMyDeals,
  saveUpd,
  sendPaper,
  updateDeal,
  type StoreResult,
  type Deal,
  type DealStep,
  type PartnerDeal,
} from "./deals-store";
import { listEntities } from "./legal-entities-store";
import { logPdAccess, readPartnerCard } from "./partners-store";
import { buildDoc, unsupported, updAddresses, updForDeal } from "./docgen/for-deal";
import { fetchAddress, readDadataKey } from "./registry-fetch";
import type { UpdView } from "./docgen/upd";
import type { DocModel } from "./docgen/model";
import {
  docKey,
  dropEditedDoc,
  editedKeys,
  readEditedDoc,
  readTemplateOverrides,
  saveEditedDoc,
  saveTemplateText,
  type DocKey,
} from "./deal-docs-store";
import { templateCatalog } from "./docgen/catalog";
import type { TemplateText } from "./docgen/texts";
import { notifyLeadsUpd } from "./partner-notify";
import { renderDocx } from "./docgen/render";

/**
 * Комплекты документов с подрядчиками (в коде — «сделки»): экран сотрудника
 * документооборота (`requireDocflow`) и список своих договоров в кабинете
 * партнёра (`requirePartner`).
 */

const ISO = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable();

const Draft = z.object({
  contractorId: z.string().uuid(),
  entityId: z.string().uuid().nullable(),
  project: z.string().max(300),
  managerId: z.string().uuid().nullable(),
  services: z.string().max(2000),
  kind: z.enum(["one_off", "frame", "annex", "supplement"]),
  contractNo: z.string().max(50),
  annexNo: z.string().max(20),
  periodFrom: ISO,
  periodTo: ISO,
  amountNet: z.number().positive().max(1e11).nullable(),
  taxMarkup: z.number().min(0).max(30).nullable(),
  ad: z.boolean(),
  markingBy: z.enum(["us", "contractor"]),
  note: z.string().max(4000),
  parentId: z.string().uuid().nullable(),
  contractDate: ISO,
  tender: z.boolean(),
  paymentScheme: z.enum(PAYMENT_SCHEMES),
  prepayPct: z.number().min(1).max(99),
  stages: z
    .array(
      z.object({
        from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        amount: z.number().positive().max(1e11).nullable(),
      }),
    )
    .max(36),
  noNda: z.boolean(),
  withAct: z.boolean(),
  withFrame: z.boolean(),
  changes: z
    .array(
      z.object({
        mode: z.enum(["restate", "replace", "info"]),
        clause: z.string().max(40),
        oldText: z.string().max(4000),
        newText: z.string().max(4000),
      }),
    )
    .max(30),
  effectiveFrom: ISO,
}) satisfies z.ZodType<DealDraft>;

export const loadDeals = createServerFn({ method: "GET" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z.object({ contractorId: z.string().uuid().optional() }).parse(data ?? {}),
  )
  .handler(async ({ data }): Promise<Deal[]> => listDeals(data));

/** Документ подрядчика, к которому можно привязать приложение или ДС. */
export interface ParentOption {
  id: string;
  kind: Deal["kind"];
  title: string;
  entityName: string | null;
}

export interface DealView {
  deal: Deal;
  /** правленые документы комплекта */
  edited: DocKey[];
  /** документы этого подрядчика — к чему может относиться приложение или ДС */
  parents: ParentOption[];
}

/** Документы подрядчика, к которым можно что-то приложить (без аннулированных). */
async function parentOptions(contractorId: string, exceptId?: string): Promise<ParentOption[]> {
  const mine = await listDeals({ contractorId });
  return mine
    .filter((d) => d.status !== "cancelled" && d.id !== exceptId)
    .map((d) => ({ id: d.id, kind: d.kind, title: d.title, entityName: d.entityName }));
}

export const loadDeal = createServerFn({ method: "GET" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data }): Promise<DealView | null> => {
    const deal = await readDeal(data.id);
    if (!deal) return null;
    return {
      deal,
      edited: await editedKeys(deal.id),
      parents: await parentOptions(deal.contractorId, deal.id),
    };
  });

/** Для мастера: к чему у подрядчика можно сделать приложение или ДС. */
export const loadParentOptions = createServerFn({ method: "GET" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) => z.object({ contractorId: z.string().uuid() }).parse(data))
  .handler(async ({ data }): Promise<ParentOption[]> => parentOptions(data.contractorId));

/** Что нужно форме комплекта и списку: юрлица агентства и кто ведёт документы. */
export const loadDealOptions = createServerFn({ method: "GET" })
  .middleware([requireDocflow])
  .handler(async () => {
    const [entities, people] = await Promise.all([listEntities(), docflowPeople()]);
    return {
      entities: entities.filter((e) => !e.archived).map((e) => ({ id: e.id, name: e.name })),
      people,
    };
  });

/** Новый комплект из мастера: ID проекта, подрядчик, юрлицо, комплектация. */
export const createKitFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z
      .object({
        contractorId: z.string().uuid(),
        project: z.string().trim().min(1).max(300),
        entityId: z.string().uuid().nullable(),
        shape: z.enum(["one_off", "frame_annex", "annex", "supplement"]),
        parentId: z.string().uuid().nullable(),
        external: z
          .object({
            contractNo: z.string().trim().min(1).max(50),
            contractDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          })
          .nullable()
          .optional(),
        withAct: z.boolean(),
        withNda: z.boolean(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }): Promise<DealStep> => createKit(data, context.auth.email));

export const updateDealFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), draft: Draft, seen: z.string().max(64) }).parse(data),
  )
  .handler(
    async ({ data, context }): Promise<DealStep> =>
      updateDeal(data.id, data.draft, data.seen, context.auth.email),
  );

export const moveDealFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        to: z.enum(DEAL_STATUSES as unknown as [DealStatus, ...DealStatus[]]),
        seen: z.string().max(64),
      })
      .parse(data),
  )
  .handler(
    async ({ data, context }): Promise<DealStep> =>
      moveDeal(data.id, data.to, data.seen, context.auth.email),
  );

/** Подрядчику — его договоры, кроме черновиков агентства. */
export const loadMyDeals = createServerFn({ method: "GET" })
  .middleware([requirePartner])
  .handler(async (): Promise<PartnerDeal[]> => readMyDeals());

export type DealDocResult =
  | { ok: true; name: string; base64: string }
  | { ok: false; error: string };

/**
 * Документ по сделке — docx, чтобы Даша поправила перед отправкой.
 * Собирается из сделки, юрлица агентства и карточки подрядчика; каждое
 * формирование пишется в журнал доступа к данным подрядчика (152-ФЗ).
 */
const DocInput = z.object({
  id: z.string().uuid(),
  doc: z.enum(["contract", "frame", "act", "nda"]),
  actNo: z.number().int().min(1).max(36).optional(),
});

/** Модель документа из редактора — те же блоки, что собирает docgen. */
const Line = z.string().max(20_000);
const Party = z.object({ title: Line, lines: z.array(Line).max(60), sign: Line });
const DocModelInput = z.object({
  name: z.string().min(1).max(200),
  blocks: z
    .array(
      z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("title"), lines: z.array(Line).max(20) }),
        z.object({ kind: z.literal("placeDate"), place: Line, date: Line }),
        z.object({
          kind: z.literal("para"),
          text: Line,
          indent: z.boolean().optional(),
          bold: z.boolean().optional(),
          center: z.boolean().optional(),
        }),
        z.object({ kind: z.literal("section"), text: Line }),
        z.object({ kind: z.literal("clause"), no: z.string().max(40), text: Line }),
        z.object({ kind: z.literal("bullets"), items: z.array(Line).max(200) }),
        z.object({
          kind: z.literal("table"),
          header: z.array(Line).max(20),
          rows: z.array(z.array(Line).max(20)).max(200),
          widths: z.array(z.number().positive().max(1000)).max(20),
        }),
        z.object({ kind: z.literal("parties"), left: Party, right: Party }),
      ]),
    )
    .max(2000),
}) satisfies z.ZodType<DocModel>;

/**
 * Документ комплекта: правленый, если Даша его правила, иначе собранный из
 * комплекта, юрлица и карточки подрядчика. Ошибка — строкой.
 */
async function dealDocModel(
  deal: Deal,
  which: z.infer<typeof DocInput>["doc"],
  actNo: number | undefined,
): Promise<
  | { ok: true; model: DocModel; edited: boolean; contractorId: string }
  | { ok: false; error: string }
> {
  const edited = await readEditedDoc(deal.id, docKey(which, actNo));
  if (edited) return { ok: true, model: edited, edited: true, contractorId: deal.contractorId };
  if (!deal.entityId) return { ok: false, error: "Выберите юрлицо агентства" };
  const entity = (await listEntities()).find((e) => e.id === deal.entityId);
  const card = await readPartnerCard(deal.contractorId);
  if (!entity || !card) return { ok: false, error: "Не нашлись юрлицо или подрядчик" };
  const chain = await readChain(deal);
  const why = unsupported(which, deal, card, chain);
  if (why) return { ok: false, error: why };
  return {
    ok: true,
    model: buildDoc(which, deal, entity, card, actNo, chain, await kitTexts(deal)),
    edited: false,
    contractorId: deal.contractorId,
  };
}

/**
 * Документ комплекта — docx, чтобы Даша поправила перед отправкой (или
 * правленый в редакторе). Каждое формирование пишется в журнал доступа к
 * данным подрядчика (152-ФЗ).
 */
export const generateDealDocFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) => DocInput.parse(data))
  .handler(async ({ data, context }): Promise<DealDocResult> => {
    const deal = await readDeal(data.id);
    if (!deal) return { ok: false, error: "Комплект не найден" };
    const got = await dealDocModel(deal, data.doc, data.actNo);
    if (!got.ok) return got;
    await logPdAccess(got.contractorId, context.auth.email, `сформировал: ${got.model.name}`);
    const buf = await renderDocx(got.model);
    return { ok: true, name: `${got.model.name}.docx`, base64: buf.toString("base64") };
  });

export type DocOpenResult =
  | { ok: true; model: DocModel; edited: boolean }
  | { ok: false; error: string };

/** Открыть документ в редакторе: правленый или собранный заново. */
export const openDealDocFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) => DocInput.parse(data))
  .handler(async ({ data, context }): Promise<DocOpenResult> => {
    const deal = await readDeal(data.id);
    if (!deal) return { ok: false, error: "Комплект не найден" };
    const got = await dealDocModel(deal, data.doc, data.actNo);
    if (!got.ok) return got;
    await logPdAccess(got.contractorId, context.auth.email, `открыл на правку: ${got.model.name}`);
    return { ok: true, model: got.model, edited: got.edited };
  });

/** Сохранить правку документа — дальше скачивается она. */
export const saveDealDocFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) => DocInput.extend({ model: DocModelInput }).parse(data))
  .handler(async ({ data, context }): Promise<StoreResult> => {
    const deal = await readDeal(data.id);
    if (!deal) return { ok: false, error: "Комплект не найден" };
    await saveEditedDoc(deal.id, docKey(data.doc, data.actNo), data.model);
    await logPdAccess(deal.contractorId, context.auth.email, `поправил: ${data.model.name}`);
    return { ok: true };
  });

/** Вернуть документ как в шаблоне — правка удаляется. */
export const resetDealDocFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) => DocInput.parse(data))
  .handler(async ({ data }): Promise<StoreResult> => {
    await dropEditedDoc(data.id, docKey(data.doc, data.actNo));
    return { ok: true };
  });

/** Подрядчик отправил подписанные оригиналы и вписал трек (созвон с Дашей 28.10). */
export const sendPaperFn = createServerFn({ method: "POST" })
  .middleware([requirePartner])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        carrier: z.enum(PAPER_CARRIERS),
        track: z.string().max(60),
      })
      .parse(data),
  )
  .handler(
    async ({ data, context }): Promise<StoreResult> =>
      sendPaper(data.id, data.carrier, data.track, context.auth.email),
  );

/** Даша отметила, что оригиналы приехали: можно отдавать офис-менеджеру. */
export const markArrivedFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), arrived: z.boolean() }).parse(data),
  )
  .handler(
    async ({ data, context }): Promise<StoreResult> =>
      markArrived(data.id, data.arrived, context.auth.email),
  );

/**
 * Акт для подрядчика на бумаге: скачать, подписать, отправить. Только по
 * своему подписанному комплекту. Читаем короткими кусками (`partnerSession`):
 * сборка docx не держит соединение пула.
 */
export const myActFn = createServerFn({ method: "POST" })
  .middleware([partnerSession])
  .inputValidator((data: unknown) =>
    z
      .object({ id: z.string().uuid(), actNo: z.number().int().min(1).max(36).optional() })
      .parse(data),
  )
  .handler(async ({ data, context }): Promise<DealDocResult> => {
    const deal = await runInUserContext(context.userId, () => myDeal(data.id));
    if (!deal) return { ok: false, error: "Документ не найден" };
    if (!closingOpen(deal.status)) return { ok: false, error: "Договор ещё не подписан" };
    const parts = await runAsServiceDetached(async () => ({
      entity: deal.entityId
        ? ((await listEntities()).find((e) => e.id === deal.entityId) ?? null)
        : null,
      card: await readPartnerCard(deal.contractorId),
      chain: await readChain(deal),
      texts: await kitTexts(deal),
    }));
    if (!parts.entity || !parts.card) {
      return { ok: false, error: "Акт пока не собрать — напишите менеджеру агентства" };
    }
    const why = unsupported("act", deal, parts.card, parts.chain);
    if (why) return { ok: false, error: why };
    // Правленый Дашей акт — тот, что подрядчик подписывает.
    const edited = await runAsServiceDetached(() =>
      readEditedDoc(deal.id, docKey("act", data.actNo)),
    );
    const model =
      edited ??
      buildDoc("act", deal, parts.entity, parts.card, data.actNo, parts.chain, parts.texts);
    const buf = await renderDocx(model);
    return { ok: true, name: `${model.name}.docx`, base64: buf.toString("base64") };
  });

export type UpdResult =
  | { ok: true; fileName: string; base64: string; view: UpdView }
  | { ok: false; error: string };

const UpdAskInput = z.object({
  id: z.string().uuid(),
  no: z.string().trim().min(1).max(40),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  results: z.string().max(500),
  stage: z.number().int().min(1).max(36),
});

/**
 * УПД в кабинете подрядчика на ЭДО (созвон с Дашей 28.10): XML для его ЭДО
 * и превью. Его ввод сохраняется — Даша откроет тот же УПД у себя — и ей
 * уходит уведомление проверить.
 */
export const makeUpdFn = createServerFn({ method: "POST" })
  .middleware([partnerSession])
  .inputValidator((data: unknown) => UpdAskInput.parse(data))
  .handler(async ({ data, context }): Promise<UpdResult> => {
    const deal = await runInUserContext(context.userId, () => myDeal(data.id));
    if (!deal) return { ok: false, error: "Документ не найден" };
    if (!closingOpen(deal.status)) return { ok: false, error: "Договор ещё не подписан" };
    const parts = await runAsServiceDetached(async () => ({
      entity: deal.entityId
        ? ((await listEntities()).find((e) => e.id === deal.entityId) ?? null)
        : null,
      card: await readPartnerCard(deal.contractorId),
      chain: await readChain(deal),
    }));
    if (!parts.entity || !parts.card) {
      return { ok: false, error: "УПД пока не собрать — напишите менеджеру агентства" };
    }
    if (!(parts.card.requisites.values.docFlow ?? "").startsWith("ЭДО")) {
      return { ok: false, error: "В реквизитах выбрана бумага — УПД через ЭДО не нужен" };
    }
    // Адреса — по полям, как в Диадоке: DaData вне транзакции (partnerSession),
    // без неё — разбор строки. Сохраняются с УПД — Даша откроет тот же XML.
    const key = await readDadataKey().catch(() => null);
    const addr = await updAddresses(parts.entity, parts.card, (text) =>
      key ? fetchAddress(text, key) : Promise.resolve(null),
    );
    const built = updForDeal(deal, parts.entity, parts.card, parts.chain, { ...data, addr });
    if (!built.ok) return built;
    await saveUpd(
      deal.id,
      {
        no: data.no,
        date: data.date,
        results: data.results,
        stage: data.stage,
        at: new Date().toISOString(),
        addr,
      },
      context.auth.email,
    );
    afterRequest("deal-upd-notify", () => notifyLeadsUpd(deal.id, parts.card!.name, data.no));
    return {
      ok: true,
      fileName: built.fileName,
      base64: Buffer.from(built.xml).toString("base64"),
      view: built.view,
    };
  });

/** Тот же УПД у Даши — проверить, пока подрядчик не отправил его в ЭДО. */
export const loadUpdFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }): Promise<UpdResult> => {
    const deal = await readDeal(data.id);
    if (!deal?.upd) return { ok: false, error: "Подрядчик ещё не формировал УПД" };
    const entity = (await listEntities()).find((e) => e.id === deal.entityId);
    const card = await readPartnerCard(deal.contractorId);
    if (!entity || !card) return { ok: false, error: "Не нашлись юрлицо или подрядчик" };
    const built = updForDeal(deal, entity, card, await readChain(deal), deal.upd);
    if (!built.ok) return built;
    await logPdAccess(card.id, context.auth.email, `открыл УПД № ${deal.upd.no}`);
    return {
      ok: true,
      fileName: built.fileName,
      base64: Buffer.from(built.xml).toString("base64"),
      view: built.view,
    };
  });

export interface TemplateRow extends TemplateText {
  /** правка Даши, если есть */
  override: string | null;
}

/** Раздел «Шаблоны»: постоянные тексты по документам и правки к ним. */
export const loadTemplatesFn = createServerFn({ method: "GET" })
  .middleware([requireDocflow])
  .handler(async (): Promise<TemplateRow[]> => {
    const overrides = await readTemplateOverrides();
    return templateCatalog().map((t) => ({ ...t, override: overrides[t.key] ?? null }));
  });

/** Сохранить правку постоянного текста; `text: null` — вернуть как было. */
export const saveTemplateFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z
      .object({ key: z.string().min(1).max(200), text: z.string().max(20_000).nullable() })
      .parse(data),
  )
  .handler(async ({ data }): Promise<StoreResult> => {
    const known = templateCatalog().some((t) => t.key === data.key);
    if (!known) return { ok: false, error: "Такого текста в шаблонах нет" };
    await saveTemplateText(data.key, data.text?.trim() ? data.text.trim() : null);
    return { ok: true };
  });

/**
 * Взять в комплект текущие шаблоны — явно и только пока он на согласовании:
 * подписанный комплект собирается по тем текстам, что ушли подрядчику.
 */
export const refreshKitTextsFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data }): Promise<StoreResult> => {
    const deal = await readDeal(data.id);
    if (!deal) return { ok: false, error: "Комплект не найден" };
    if (deal.status !== "approval") {
      return { ok: false, error: "Обновить тексты можно, пока комплект на согласовании" };
    }
    await freezeTexts(deal.id);
    return { ok: true };
  });
